import { useParams, Link, Navigate } from "react-router-dom";
import { Check, MapPin, Phone } from "lucide-react";
import { CITIES, SERVICE_DETAILS, FAQS, IMAGES, BUSINESS } from "@/data/site";
import { usePageMeta, PageHero, Breadcrumb, SectionLabel, PrimaryButton, CTABanner } from "@/components/common";

const SERVICE_KEYS = ["stucco-repair", "new-stucco-installation", "stucco-textures-finishes", "plaster-interior-finishes", "residential-stucco", "commercial-stucco"];

export default function LocationDetail() {
  const { slug } = useParams();
  const city = CITIES.find((c) => c.slug === slug);
  usePageMeta(city ? `Stucco Contractor in ${city.name}, FL` : "Location", city ? `A.D.N Stucco Inc is your trusted stucco contractor in ${city.name}, FL. Stucco repair, installation, textures & plaster. Licensed, insured & free estimates.` : "");
  if (!city) return <Navigate to="/service-areas" replace />;

  return (
    <div data-testid="location-page">
      <PageHero label={`${city.name}, Florida`} title={`Stucco Contractor in ${city.name}, FL`} subtitle={`Licensed & insured stucco and plastering services for homeowners and businesses throughout ${city.name}. Quality craftsmanship, honest pricing, free estimates.`} image={IMAGES.hero} />
      <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Service Areas", to: "/service-areas" }, { label: `${city.name}, FL` }]} />

      <section className="py-24 bg-adn-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <SectionLabel>Your Local Stucco Experts</SectionLabel>
            <h2 className="text-3xl sm:text-4xl font-heading font-extrabold mt-6 leading-tight">Trusted stucco &amp; plaster services in {city.name}</h2>
            <p className="text-lg text-zinc-700 mt-6 leading-relaxed">A.D.N Stucco Inc proudly serves the {city.name} community with expert stucco installation, repair, remediation, and interior &amp; exterior plaster. Florida's heat, humidity, and storms can be tough on exteriors — our weather-resistant finishes and meticulous craftsmanship keep your {city.name} property protected and looking its best for decades.</p>
            <p className="text-lg text-zinc-700 mt-4 leading-relaxed">As a locally trusted stucco company, we understand the unique needs of {city.name} homes and businesses. From new construction to crack repair and waterproofing, we deliver reliable communication, clean job sites, and finishes built to last. Every {city.name} project starts with a free, no-obligation estimate.</p>

            <h3 className="text-2xl font-heading font-bold mt-12">Stucco services available in {city.name}</h3>
            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              {SERVICE_KEYS.map((k) => (
                <Link key={k} to={`/services/${k}`} className="flex items-center gap-3 border border-zinc-200 bg-white p-4 hover:border-adn-red transition-colors">
                  <Check size={20} className="text-adn-red shrink-0" /><span className="font-medium text-zinc-700">{SERVICE_DETAILS[k].title}</span>
                </Link>
              ))}
            </div>

            <h3 className="text-2xl font-heading font-bold mt-12">{city.name} stucco FAQs</h3>
            <div className="space-y-4 mt-6">
              {FAQS.slice(0, 4).map((f) => (
                <div key={f.q} className="bg-white border border-zinc-200 p-6">
                  <h4 className="font-heading font-bold">{f.q}</h4>
                  <p className="text-zinc-600 mt-2 leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </div>

          <aside>
            <div className="sticky top-28 space-y-6">
              <div className="bg-adn-charcoal text-white p-8">
                <MapPin size={28} className="text-adn-red" />
                <h3 className="text-2xl font-heading font-bold mt-4">Free Estimate in {city.name}</h3>
                <p className="text-zinc-300 mt-3 text-sm leading-relaxed">Serving {city.name} and all of Central Florida. Get your free quote today.</p>
                <div className="mt-6"><PrimaryButton to="/contact" testid="location-cta" className="w-full">Request Estimate</PrimaryButton></div>
                <a href={BUSINESS.phoneHref} className="mt-4 flex items-center justify-center gap-2 border-2 border-white text-white py-3 font-semibold hover:bg-white hover:text-adn-charcoal transition-colors"><Phone size={16} /> {BUSINESS.phone}</a>
              </div>
              <div className="border border-zinc-200 overflow-hidden">
                <iframe title={`${city.name} map`} src={BUSINESS.mapEmbed} width="100%" height="260" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
              </div>
            </div>
          </aside>
        </div>
      </section>
      <CTABanner title={`Ready for Quality Stucco in ${city.name}?`} />
    </div>
  );
}
